import { hierarchy } from '@visx/hierarchy';
import {
  faGauge,
  faGaugeHigh,
  faGaugeLow,
  faGaugeMax,
  faGaugeMin,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faGauge as faGaugeSolid,
  faGaugeHigh as faGaugeHighSolid,
  faGaugeLow as faGaugeLowSolid,
  faGaugeMax as faGaugeMaxSolid,
  faGaugeMin as faGaugeMinSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { scaleColorTraffic } from 'js/utils/color';

export const TEXT_FOREGROUND_ALPHA = 0.8;
export const TEXT_DARK_HEX = '#2c2c2c';
export const TILE_INSET = 2;
export const LABEL_INSET = 8;
export const SET_HEADER_HEIGHT_WITH_METRICS = 56;
export const SET_HEADER_HEIGHT_TITLE_ONLY = 30;
export const QUESTION_TILE_GAP = 1.5;
export const QUESTION_AREA_BORDER = 'rgba(255, 255, 255, 0.6)';
export const QUESTION_AREA_RADIUS = 2;
export const QUESTION_SET_RADIUS = 4;

export function toFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function formatWeight(weight) {
  const finiteWeight = toFiniteNumber(weight);
  if (finiteWeight == null) {
    return 'unweighted';
  }
  return Number.isInteger(finiteWeight)
    ? finiteWeight.toString()
    : finiteWeight.toFixed(2);
}

export function formatWeightFraction(weight, totalWeight) {
  const finiteWeight = toFiniteNumber(weight);
  const finiteTotalWeight = toFiniteNumber(totalWeight);
  if (
    finiteWeight == null ||
    finiteTotalWeight == null ||
    finiteTotalWeight <= 0
  ) {
    return 'Unweighted';
  }

  const formattedWeight = Number.isInteger(finiteWeight)
    ? finiteWeight.toString()
    : finiteWeight.toFixed(2);
  const formattedTotal = Number.isInteger(finiteTotalWeight)
    ? finiteTotalWeight.toString()
    : finiteTotalWeight.toFixed(2);

  return `${formattedWeight}/${formattedTotal}`;
}

export function formatScore(score) {
  const finiteScore = toFiniteNumber(score);
  return finiteScore == null
    ? 'Unscored'
    : `${(finiteScore * 100).toFixed(1)}%`;
}

export function scoreFill(score) {
  const finiteScore = toFiniteNumber(score);
  if (finiteScore == null) {
    return '#6c757d';
  }
  return scaleColorTraffic(Math.max(0, Math.min(1, finiteScore)));
}

export function scoreIcon(score, { solid = false } = {}) {
  const finiteScore = toFiniteNumber(score);
  const icons = solid
    ? {
        gauge: faGaugeSolid,
        gaugeMin: faGaugeMinSolid,
        gaugeLow: faGaugeLowSolid,
        gaugeHigh: faGaugeHighSolid,
        gaugeMax: faGaugeMaxSolid,
      }
    : {
        gauge: faGauge,
        gaugeMin: faGaugeMin,
        gaugeLow: faGaugeLow,
        gaugeHigh: faGaugeHigh,
        gaugeMax: faGaugeMax,
      };
  if (finiteScore == null) {
    return icons.gauge;
  } else if (finiteScore < 0.2) {
    return icons.gaugeMin;
  } else if (finiteScore < 0.4) {
    return icons.gaugeLow;
  } else if (finiteScore < 0.6) {
    return icons.gauge;
  } else if (finiteScore < 0.8) {
    return icons.gaugeHigh;
  }
  return icons.gaugeMax;
}

export function scoreIconColor(score, { colorize = false } = {}) {
  if (!colorize) {
    return undefined;
  }
  return scoreFill(score);
}

export function buildQuestionRoot(questions) {
  const weightedQuestions = (questions || [])
    .map((questionNode) => ({
      ...questionNode,
      areaWeight: toFiniteNumber(questionNode.weight),
    }))
    .filter(
      (questionNode) =>
        questionNode.areaWeight != null && questionNode.areaWeight > 0
    );

  const totalWeight = weightedQuestions.reduce(
    (sum, questionNode) => sum + questionNode.areaWeight,
    0
  );

  const children = weightedQuestions.map((questionNode) => ({
    ...questionNode,
    totalSetWeight: totalWeight,
  }));

  return hierarchy({ name: 'questions', children })
    .sum((d) => d.areaWeight || 0)
    .sort((a, b) => b.value - a.value);
}
